import { useEffect, useState } from "react";

/**
 * The 24-hour customer service window.
 *
 * Primary UI, not a detail (.docs/04 §7). Inside it a free-form reply is free;
 * outside it only an approved template goes out, and it is billed per message.
 * Sellers triage by this timer once they understand it, which is the behaviour
 * the product wants — so it is shown in the list *and* in the thread, live.
 */
export type WindowState = {
  isOpen: boolean;
  msRemaining: number;
  /** "19h 42m", "42m", "3m". Empty when closed or never opened. */
  label: string;
  urgency: "closed" | "soon" | "warning" | "fine";
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

export function windowState(
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): WindowState {
  const closed: WindowState = {
    isOpen: false,
    msRemaining: 0,
    label: "",
    urgency: "closed",
  };
  if (!expiresAt) return closed;

  const expiry = new Date(expiresAt).getTime();
  if (Number.isNaN(expiry)) return closed;

  const msRemaining = expiry - now;
  if (msRemaining <= 0) return closed;

  const hours = Math.floor(msRemaining / HOUR);
  const minutes = Math.floor((msRemaining % HOUR) / MINUTE);

  return {
    isOpen: true,
    msRemaining,
    label: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
    // One hour is roughly "answer it now or write a template"; four is
    // "today". Both thresholds are about what the seller should do next, not
    // about the arithmetic.
    urgency: msRemaining < HOUR ? "soon" : msRemaining < 4 * HOUR ? "warning" : "fine",
  };
}

export const URGENCY_TONES: Record<WindowState["urgency"], string> = {
  closed: "text-mute-400",
  soon: "text-brand",
  warning: "text-brand-700",
  fine: "text-mute-600",
};

/**
 * Re-render as the window drains.
 *
 * One timer per mounted countdown, at one tick a minute — the label has
 * minute resolution, so a per-second tick would re-render the whole
 * conversation list sixty times for every visible change. Under the last
 * minute it drops to a second so the transition to "cerrada" is not late.
 */
export function useWindowState(expiresAt: string | null | undefined): WindowState {
  const [now, setNow] = useState(() => Date.now());
  const state = windowState(expiresAt, now);
  const isFinalMinute = state.isOpen && state.msRemaining < MINUTE;

  useEffect(() => {
    if (!expiresAt) return;
    const timer = setInterval(() => setNow(Date.now()), isFinalMinute ? 1_000 : MINUTE);
    return () => clearInterval(timer);
    // Only `isFinalMinute` may change the cadence. Depending on the raw
    // remaining milliseconds would tear down and rebuild the timer on every
    // single tick.
  }, [expiresAt, isFinalMinute]);

  return state;
}
